import React, { memo, useCallback } from 'react';
import {
  DONATION_TYPES,
  PAYMENT_METHODS,
  FREQUENCIES,
  SUBSCRIPTION_END_TYPES,
  DROP_OFF_TIME_SLOTS,
  TEMPLE_ADDRESS,
  formatCardNumber,
  formatExpiry,
  formatZip,
  formatCvv,
} from '../models/groceryModels';
import RecaptchaBox from '../../../components/RecaptchaBox';
import './GroceryComponents.css';

const todayISO = () => new Date().toISOString().split('T')[0];

const GroceryPaymentStep = ({
  donationType,
  paymentMethod,
  frequency,
  card,
  ach,
  recurring,
  dropOff,
  errors,
  onDonationTypeChange,
  onPaymentMethodChange,
  onFrequencyChange,
  onCardChange,
  onAchChange,
  onRecurringChange,
  onDropOffChange,
  onRecaptchaChange,
  recaptchaRef,
}) => {
  const isPhysical = donationType === DONATION_TYPES.PHYSICAL;

  const handleCard = useCallback(
    (field, formatter) => (event) =>
      onCardChange(field, formatter ? formatter(event.target.value) : event.target.value),
    [onCardChange]
  );

  const handleAch = useCallback(
    (field) => (event) => onAchChange(field, event.target.value),
    [onAchChange]
  );

  const fieldClass = (field, base) => `${base}${errors[field] ? ' has-error' : ''}`;

  return (
    <div>
      {/* ── Donation type ─────────────────────────────────────────────── */}
      <div className="grocery-section">
        <div className="grocery-section-title">How would you like to donate?</div>
        <div className="grocery-option-grid">
          <button
            type="button"
            className={`grocery-option${!isPhysical ? ' is-selected' : ''}`}
            onClick={() => onDonationTypeChange(DONATION_TYPES.ONLINE)}
          >
            <div className="grocery-option-title">
              <span role="img" aria-hidden="true">{'\uD83D\uDCB3'}</span> Pay Online
            </div>
            <div className="grocery-option-desc">
              We purchase the groceries on your behalf.
            </div>
          </button>

          <button
            type="button"
            className={`grocery-option${isPhysical ? ' is-selected' : ''}`}
            onClick={() => onDonationTypeChange(DONATION_TYPES.PHYSICAL)}
          >
            <div className="grocery-option-title">
              <span role="img" aria-hidden="true">{'\uD83D\uDCE6'}</span> Drop Off Items
            </div>
            <div className="grocery-option-desc">
              Bring the groceries to the temple yourself.
            </div>
          </button>
        </div>
      </div>

      {/* ── Physical drop-off ─────────────────────────────────────────── */}
      {isPhysical && (
        <div className="grocery-section">
          <div className="grocery-callout">
            <div className="grocery-callout-title">Temple Address for Drop-Off</div>
            <div className="grocery-callout-body">
              {TEMPLE_ADDRESS.name}
              <br />
              {TEMPLE_ADDRESS.street}
            </div>
          </div>

          <div className="grocery-form-grid">
            <div className="grocery-field">
              <label className="grocery-label" htmlFor="grocery-dropoff-date">
                Drop-Off Date <span className="grocery-required">*</span>
              </label>
              <input
                id="grocery-dropoff-date"
                type="date"
                min={todayISO()}
                className={fieldClass('dropOffDate', 'grocery-input')}
                value={dropOff.date}
                onChange={(event) => onDropOffChange('date', event.target.value)}
              />
              {errors.dropOffDate && <span className="grocery-error-text">{errors.dropOffDate}</span>}
            </div>

            <div className="grocery-field">
              <label className="grocery-label" htmlFor="grocery-dropoff-time">
                Preferred Time <span className="grocery-required">*</span>
              </label>
              <select
                id="grocery-dropoff-time"
                className={fieldClass('dropOffTime', 'grocery-select')}
                value={dropOff.time}
                onChange={(event) => onDropOffChange('time', event.target.value)}
              >
                <option value="">Select a time slot</option>
                {DROP_OFF_TIME_SLOTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
              {errors.dropOffTime && <span className="grocery-error-text">{errors.dropOffTime}</span>}
            </div>
          </div>
        </div>
      )}

      {/* ── Online payment ───────────────────────────────────────────── */}
      {!isPhysical && (
        <>
          <div className="grocery-section">
            <div className="grocery-section-title">Donation Frequency</div>
            <div className="grocery-option-grid">
              <button
                type="button"
                className={`grocery-option${frequency === FREQUENCIES.ONETIME ? ' is-selected' : ''}`}
                onClick={() => onFrequencyChange(FREQUENCIES.ONETIME)}
              >
                <div className="grocery-option-title">One-Time</div>
                <div className="grocery-option-desc">A single donation today.</div>
              </button>

              <button
                type="button"
                className={`grocery-option${frequency === FREQUENCIES.RECURRING ? ' is-selected' : ''}`}
                onClick={() => onFrequencyChange(FREQUENCIES.RECURRING)}
              >
                <div className="grocery-option-title">Monthly Recurring</div>
                <div className="grocery-option-desc">Repeat this donation every month.</div>
              </button>
            </div>
          </div>

          {frequency === FREQUENCIES.RECURRING && (
            <div className="grocery-section">
              <div className="grocery-section-title">Recurring Schedule</div>
              <div className="grocery-form-grid">
                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-recurring-start">
                    Start Date <span className="grocery-required">*</span>
                  </label>
                  <input
                    id="grocery-recurring-start"
                    type="date"
                    min={todayISO()}
                    className={fieldClass('recurringStartDate', 'grocery-input')}
                    value={recurring.startDate}
                    onChange={(event) => onRecurringChange('startDate', event.target.value)}
                  />
                  {errors.recurringStartDate && (
                    <span className="grocery-error-text">{errors.recurringStartDate}</span>
                  )}
                </div>

                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-recurring-endtype">
                    Ends
                  </label>
                  <select
                    id="grocery-recurring-endtype"
                    className="grocery-select"
                    value={recurring.endType}
                    onChange={(event) => onRecurringChange('endType', event.target.value)}
                  >
                    <option value={SUBSCRIPTION_END_TYPES.NO_END}>Never</option>
                    <option value={SUBSCRIPTION_END_TYPES.ON_DATE}>On a specific date</option>
                    <option value={SUBSCRIPTION_END_TYPES.AFTER_TERMS}>After a number of payments</option>
                  </select>
                </div>

                {recurring.endType === SUBSCRIPTION_END_TYPES.ON_DATE && (
                  <div className="grocery-field">
                    <label className="grocery-label" htmlFor="grocery-recurring-end">
                      End Date <span className="grocery-required">*</span>
                    </label>
                    <input
                      id="grocery-recurring-end"
                      type="date"
                      min={recurring.startDate || todayISO()}
                      className={fieldClass('recurringEndDate', 'grocery-input')}
                      value={recurring.endDate}
                      onChange={(event) => onRecurringChange('endDate', event.target.value)}
                    />
                    {errors.recurringEndDate && (
                      <span className="grocery-error-text">{errors.recurringEndDate}</span>
                    )}
                  </div>
                )}

                {recurring.endType === SUBSCRIPTION_END_TYPES.AFTER_TERMS && (
                  <div className="grocery-field">
                    <label className="grocery-label" htmlFor="grocery-recurring-terms">
                      Number of Payments <span className="grocery-required">*</span>
                    </label>
                    <input
                      id="grocery-recurring-terms"
                      type="number"
                      min="1"
                      className={fieldClass('numberOfTerms', 'grocery-input')}
                      value={recurring.numberOfTerms}
                      onChange={(event) => onRecurringChange('numberOfTerms', event.target.value)}
                    />
                    {errors.numberOfTerms && (
                      <span className="grocery-error-text">{errors.numberOfTerms}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grocery-section">
            <div className="grocery-section-title">Payment Method</div>
            <div className="grocery-option-grid">
              <button
                type="button"
                className={`grocery-option${paymentMethod === PAYMENT_METHODS.CARD ? ' is-selected' : ''}`}
                onClick={() => onPaymentMethodChange(PAYMENT_METHODS.CARD)}
              >
                <div className="grocery-option-title">Credit / Debit Card</div>
                <div className="grocery-option-desc">Visa, Mastercard, Amex, Discover.</div>
              </button>

              <button
                type="button"
                className={`grocery-option${paymentMethod === PAYMENT_METHODS.ACH ? ' is-selected' : ''}`}
                onClick={() => onPaymentMethodChange(PAYMENT_METHODS.ACH)}
              >
                <div className="grocery-option-title">Bank Account (ACH)</div>
                <div className="grocery-option-desc">Pay directly from your bank.</div>
              </button>
            </div>
            {errors.paymentMethod && <span className="grocery-error-text">{errors.paymentMethod}</span>}
          </div>

          {paymentMethod === PAYMENT_METHODS.CARD && (
            <div className="grocery-section">
              <div className="grocery-section-title">Card Details</div>
              <div className="grocery-form-grid">
                <div className="grocery-field grocery-field-full">
                  <label className="grocery-label" htmlFor="grocery-card-number">
                    Card Number <span className="grocery-required">*</span>
                  </label>
                  <input
                    id="grocery-card-number"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    placeholder="1234 5678 9012 3456"
                    className={fieldClass('cardNumber', 'grocery-input')}
                    value={card.cardNumber}
                    onChange={handleCard('cardNumber', formatCardNumber)}
                  />
                  {errors.cardNumber && <span className="grocery-error-text">{errors.cardNumber}</span>}
                </div>

                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-card-expiry">
                    Expiry (MM/YY) <span className="grocery-required">*</span>
                  </label>
                  <input
                    id="grocery-card-expiry"
                    inputMode="numeric"
                    autoComplete="cc-exp"
                    placeholder="MM/YY"
                    className={fieldClass('expiryDate', 'grocery-input')}
                    value={card.expiryDate}
                    onChange={handleCard('expiryDate', formatExpiry)}
                  />
                  {errors.expiryDate && <span className="grocery-error-text">{errors.expiryDate}</span>}
                </div>

                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-card-cvv">
                    CVV <span className="grocery-required">*</span>
                  </label>
                  <input
                    id="grocery-card-cvv"
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    placeholder="123"
                    maxLength={4}
                    className={fieldClass('cvv', 'grocery-input')}
                    value={card.cvv}
                    onChange={handleCard('cvv', formatCvv)}
                  />
                  {errors.cvv && <span className="grocery-error-text">{errors.cvv}</span>}
                </div>

                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-card-zip">
                    Billing ZIP (US) <span className="grocery-required">*</span>
                  </label>
                  <input
                    id="grocery-card-zip"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    placeholder="12345"
                    maxLength={10}
                    className={fieldClass('billingZipCode', 'grocery-input')}
                    value={card.billingZipCode}
                    onChange={handleCard('billingZipCode', formatZip)}
                  />
                  {errors.billingZipCode && (
                    <span className="grocery-error-text">{errors.billingZipCode}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {paymentMethod === PAYMENT_METHODS.ACH && (
            <div className="grocery-section">
              <div className="grocery-section-title">Bank Details</div>
              <div className="grocery-form-grid">
                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-ach-type">
                    Account Type
                  </label>
                  <select
                    id="grocery-ach-type"
                    className="grocery-select"
                    value={ach.accountType}
                    onChange={handleAch('accountType')}
                  >
                    <option value="Checking">Checking</option>
                    <option value="Savings">Savings</option>
                  </select>
                </div>

                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-ach-bank">
                    Bank Name <span className="grocery-required">*</span>
                  </label>
                  <input
                    id="grocery-ach-bank"
                    className={fieldClass('bankName', 'grocery-input')}
                    value={ach.bankName}
                    onChange={handleAch('bankName')}
                  />
                  {errors.bankName && <span className="grocery-error-text">{errors.bankName}</span>}
                </div>

                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-ach-routing">
                    Routing Number <span className="grocery-required">*</span>
                  </label>
                  <input
                    id="grocery-ach-routing"
                    inputMode="numeric"
                    maxLength={9}
                    className={fieldClass('routingNumber', 'grocery-input')}
                    value={ach.routingNumber}
                    onChange={handleAch('routingNumber')}
                  />
                  {errors.routingNumber && (
                    <span className="grocery-error-text">{errors.routingNumber}</span>
                  )}
                </div>

                <div className="grocery-field">
                  <label className="grocery-label" htmlFor="grocery-ach-account">
                    Account Number <span className="grocery-required">*</span>
                  </label>
                  <input
                    id="grocery-ach-account"
                    inputMode="numeric"
                    className={fieldClass('accountNumber', 'grocery-input')}
                    value={ach.accountNumber}
                    onChange={handleAch('accountNumber')}
                  />
                  {errors.accountNumber && (
                    <span className="grocery-error-text">{errors.accountNumber}</span>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Verification ─────────────────────────────────────────── */}
      <div className="grocery-section">
        <div className="grocery-section-title">Verification</div>
        <RecaptchaBox
          ref={recaptchaRef}
          onChange={onRecaptchaChange}
          error={errors.recaptcha}
        />
      </div>
    </div>
  );
};

export default memo(GroceryPaymentStep);
